/* eslint-disable max-lines */
import { message as $message } from 'antd'
import { Zip, ZipDeflate, ZipPassThrough } from 'fflate'
import { createQueue } from 'gyl-common/utils/queue'
import streamSaver from 'gyl-common/utils/streamSaver'
import { fileTypeFromBuffer } from 'gyl-common/utils/fileType'

import transUrl from '../utils/transUrl'

import { getPackageName, getTotalSize, getExtension } from '../utils/package'

interface ErrorData {
    uid: string
    infos: {
        id: string
        payload: {
            exception: string
            file: any
        }
    }[]
}

export default class Downloader {
    options: any = {
        fetch,
    }
    queue: any = []
    errors: ErrorData[] = []

    constructor(config: any) {
        this.options = {
            ...this.options,
            ...config,
        }
    }

    // 更新队列
    _updateQueue(uid: string, payload: any) {
        const { onChange } = this.options

        this.queue = this.queue.map((v: any) => {
            if (v.uid === uid) {
                return {
                    ...v,
                    ...payload,
                }
            }

            return v
        })

        onChange && onChange(this.queue)
    }
    // 更新错误信息队列
    _updateErrors(uid: string, id: string, payload: any) {
        const findUidIndex = this.errors.findIndex(error => uid === error?.uid)
        if (findUidIndex > -1) {
            const curError = this.errors[findUidIndex]?.infos
            const findIdIndex = curError?.findIndex(error => id === error?.id)
            const curIndex = findIdIndex > -1 ? findIdIndex : curError?.length
            this.errors[findUidIndex].infos[curIndex] = {
                ...(this.errors[findUidIndex].infos[curIndex] || {}),
                id,
                payload,
            }
            return
        }
        this.errors.push({
            uid,
            infos: [
                {
                    id,
                    payload,
                },
            ],
        })
    }

    _findQueue(uid: string) {
        return this.queue.find((v: any) => v.uid === uid)
    }

    _checkTask(task: any) {
        const target = this._findQueue(task.uid)

        if (!target) {
            return false
        }

        // 中断
        return target.status === 'process'
    }

    // 包名
    _getPackageName = e => {
        const { packageName } = this.options

        if (packageName) {
            if (typeof packageName === 'function') {
                return packageName(e)
            }

            return packageName
        }

        return getPackageName(e)
    }

    // 更新配置
    setOptions(newConfig: any) {
        Object.assign(this.options, newConfig)
    }

    // 处理文件
    processFile(e: any) {
        const { processLimit } = this.options

        const onProcess = async (file: any, next: Function) => {
            // @ts-ignore
            const fileStream = streamSaver.createWriteStream(file.name, {
                size: file.size,
            })

            const { fetch } = this.options

            const readableStream: any = await fetch(transUrl(file.url)).then(res => res.body)

            if (window.WritableStream && readableStream.pipeTo) {
                return readableStream.pipeTo(fileStream)
            }

            const writer = fileStream.getWriter()

            const reader = readableStream.getReader()

            while (true) {
                try {
                    const { done, value } = await reader.read()

                    if (done) {
                        writer.close()
                        next(file)
                        break
                    } else {
                        writer.write(value)
                    }
                } catch (exception) {
                    console.error('catchError', exception)

                    writer.close()
                    next(file)
                    break
                }
            }
        }

        // 下载任务
        const task: any = createQueue({
            key: 'id',
            processLimit,
            onProcess,
        })

        task.push(e)
    }

    // 处理多文件
    processZip(e: any) {
        const { processLimit } = this.options

        // 开始
        const onStart = () => {
            this._updateQueue(task.uid, {
                status: 'process',
            })
        }

        // 处理
        const onProcess = async (file: any, next: Function) => {
            const goNext = () => {
                const target = this._findQueue(task.uid)

                // 完成的数量
                const doneNum = (target.doneNum || 0) + 1

                this._updateQueue(task.uid, {
                    status: 'process',
                    // eslint-disable-next-line no-magic-numbers
                    percent: Number(((doneNum / e.length) * 99).toFixed(2)),
                    doneNum,
                })

                next()
            }
            try {
                // 如果并发数为多个，则不能使用流的方式
                if (processLimit > 1) {
                    const { fetch } = this.options

                    const arrayBuffer: any = await fetch(transUrl(file.url)).then(res => res.arrayBuffer())

                    if (!this._checkTask(task)) {
                        return
                    }

                    const bytes = new Uint8Array(arrayBuffer)

                    let fullPath = file.path

                    try {
                        const ext = getExtension(file.path)

                        // 处理文件名中无后缀情况
                        if (!ext) {
                            const info = await fileTypeFromBuffer(arrayBuffer)

                            if (info?.ext) {
                                fullPath = `${file.path}.${info.ext}`
                            }
                        }
                    } catch (exp) {
                        console.error('catchError', exp)
                    }

                    // 添加文件
                    const zFile = new ZipPassThrough(fullPath)
                    zip.add(zFile)

                    // 整体写入
                    zFile.push(bytes, true)

                    goNext()
                    return
                }

                // 文件流
                const readableStream: any = await fetch(file.url).then(res => res.body)

                if (!this._checkTask(task)) {
                    return
                }

                // 添加文件
                const zFile = new ZipDeflate(file.path)
                zip.add(zFile)

                const render = readableStream.getReader()

                // 分片写入
                while (true) {
                    try {
                        const { done, value } = await render.read()

                        // 结束的时候，value给个空的就好
                        zFile.push(value || new Uint8Array(), done)

                        if (done) {
                            goNext()
                            break
                        }
                    } catch (exception) {
                        console.error('catchError', exception)

                        zFile.push(new Uint8Array(), true)

                        goNext()
                        break
                    }
                }
            } catch (exception) {
                console.error('catchError', exception)
                this._updateErrors(task.uid, file?.id, {
                    exception,
                    file,
                })
                goNext()
            }
        }

        // 暂停
        const onStop = () => {
            this._updateQueue(task.uid, {
                status: 'stop',
            })
        }

        // 取消
        const onCancel = () => {
            this._updateQueue(task.uid, {
                status: 'error',
                message: '已取消',
                doneNum: 0,
            })
        }

        // 结束
        const onFinish = () => {
            const target = this._findQueue(task.uid)
            const findError = this.errors?.find(error => error?.uid === task?.uid)
            if (findError) {
                $message.warn('图片异常，数据包下载不完整，请联系供应商。')
            }
            if (!target || !this._checkTask(task)) {
                return
            }

            this._updateQueue(task.uid, {
                status: 'done',
                percent: 100,
            })

            zip.end()
        }

        // 下载任务
        const task: any = createQueue({
            key: 'id',
            processLimit,
            onStart,
            onProcess,
            onStop,
            onCancel,
            onFinish,
        })

        const start = () => {
            const target = this.queue.find((v: any) => v.uid === task.uid)

            if (!target) {
                return
            }

            if (target.doneNum >= e.length) {
                this._updateQueue(task.uid, {
                    status: 'process',
                })

                onFinish()
            } else {
                task.start()
            }
        }

        const packageName = this._getPackageName({
            files: e,
        })
        const packageSize = getTotalSize({
            files: e,
        })

        // 创建可写流
        // @ts-ignore
        const fileStream = streamSaver.createWriteStream(packageName, {
            size: packageSize,
        })
        const writer = fileStream.getWriter()

        // zip 实例
        const zip = new Zip((err, dat, final) => {
            if (!err) {
                writer.write(dat)
                if (final) {
                    writer.close()
                }
            } else {
                writer.close()
            }
        })

        const per: any = {
            uid: task.uid,
            name: packageName,
            size: packageSize,
            task: {
                ...task,
                start,
            },
        }

        this.queue.push(per)

        this._updateQueue(task.uid, {
            status: 'init',
            percent: 0,
        })

        task.push(e)
    }

    // 清空
    clear() {
        this.queue.length = 0
    }
}
